<template>
  <div class="pi-obf" role="group" aria-label="Filtros da ativação">
    <div class="pi-obf__row">
      <div class="pi-obf__field">
        <span class="pi-obf__label pi-obf__labelled">
          <span :id="ids.period">Vendas de</span>
          <pi-definition-tooltip info-key="filter.salePeriod" label="Vendas de"/>
        </span>
        <div class="pi-toggle" role="group" :aria-labelledby="ids.period">
          <button v-for="preset in presets" :key="preset.value" type="button" class="pi-toggle__btn"
                  :class="{ 'is-on': !customOpen && period.preset === preset.value }"
                  :aria-pressed="!customOpen && period.preset === preset.value ? 'true' : 'false'"
                  @click="choosePreset(preset.value)">{{ preset.label }}</button>
          <button type="button" class="pi-toggle__btn" :class="{ 'is-on': customOpen }" :aria-pressed="customOpen ? 'true' : 'false'"
                  @click="openCustom">Personalizado</button>
        </div>
        <date-range-filter v-if="customOpen" class="pi-obf__range" label="Data da venda" :from="state.saleFrom" :to="state.saleTo"
                           @update:from="setRange($event, state.saleTo)" @update:to="setRange(state.saleFrom, $event)"/>
        <span class="pi-obf__hint">{{ periodText }}{{ customOpen ? ' · até ' + maxDays + ' dias' : ' · até ontem' }}</span>
        <span v-if="period.error" class="pi-obf__error" role="alert">{{ period.error }} Mostrando as vendas de {{ fallbackText }}.</span>
      </div>
      <span class="pi-obf__labelled">
        <label class="pi-obf__check">
          <input type="checkbox" :checked="compareOn" @change="setCompare($event.target.checked)">
          Comparar com o período anterior
          <span v-if="compareOn && previousText" class="pi-obf__hint">({{ previousText }})</span>
        </label>
        <pi-definition-tooltip info-key="filter.compare" label="Comparar com o período anterior"/>
      </span>
    </div>

    <div class="pi-obf__row">
      <filter-select label="Coorte" :value="state.cohort" :options="cohortOptions" all-label="Mês" @input="set('cohort', $event)">
        <pi-definition-tooltip slot="info" class="pi-obf__info" info-key="filter.cohort" label="Coorte"/>
      </filter-select>
      <filter-select label="Universo" :value="state.universe" :options="universeOptions" all-label="Vendidas" @input="set('universe', $event)">
        <pi-definition-tooltip slot="info" class="pi-obf__info" info-key="filter.universe" label="Universo"/>
      </filter-select>
      <filter-select label="Situação hoje" :value="state.status" :options="statusOptions" all-label="Todas" @input="set('status', $event)">
        <pi-definition-tooltip slot="info" class="pi-obf__info" info-key="filter.status" label="Situação hoje"/>
      </filter-select>
      <filter-select label="Trial antes da venda" :value="state.trial" :options="trialOptions" all-label="Todas" @input="set('trial', $event)">
        <pi-definition-tooltip slot="info" class="pi-obf__info" info-key="filter.trial" label="Trial antes da venda"/>
      </filter-select>
      <span class="pi-obf__labelled pi-obf__check--inline">
        <label class="pi-obf__check">
          <input type="checkbox" :checked="state.started === '1'" @change="set('started', $event.target.checked ? '1' : '')">
          Só as que iniciaram o uso
        </label>
        <pi-definition-tooltip info-key="filter.started" label="Só as que iniciaram o uso"/>
      </span>
    </div>

    <div class="pi-obf__row">
      <filter-select label="Plano (hoje)" :value="state.plan" :options="planOptions" all-label="Todos" @input="set('plan', $event)">
        <pi-definition-tooltip slot="info" class="pi-obf__info" info-key="filter.plan" label="Plano (hoje)"/>
      </filter-select>
      <filter-select label="Jornada (hoje)" :value="state.journeyNow" :options="journeyOptions" all-label="Todas" @input="set('journeyNow', $event)">
        <pi-definition-tooltip slot="info" class="pi-obf__info" info-key="filter.journeyNow" label="Jornada (hoje)"/>
      </filter-select>
      <filter-select label="Origem" :value="state.origin" :options="originOptions" all-label="Todas" @input="set('origin', $event)">
        <pi-definition-tooltip slot="info" class="pi-obf__info" info-key="filter.origin" label="Origem"/>
      </filter-select>
      <div class="pi-obf__field">
        <span class="pi-obf__label pi-obf__labelled">
          <label class="pi-obf__label" :for="ids.shard">Banco (shard)</label>
          <pi-definition-tooltip info-key="filter.shard" label="Banco (shard)"/>
        </span>
        <input :id="ids.shard" type="text" class="form-control pi-input pi-obf__shard" maxlength="8" placeholder="Ex.: NNNN"
               :value="state.shard" @change="set('shard', $event.target.value.trim().toUpperCase())" @keydown.enter.prevent="$event.target.blur()">
      </div>
    </div>
    <p class="pi-obf__note">{{ startedNote }}</p>
  </div>
</template>

<script>
import dateRangeFilter from "../default/dateRangeFilter.vue";
import filterSelect from "../default/filterSelect.vue";
import piDefinitionTooltip from "./piDefinitionTooltip.vue";
import { JOURNEY_OPTIONS, applyCompare, compareEnabled } from "./piQuery.js";
import {
  COHORT_OPTIONS, MAX_SALE_RANGE_DAYS, ORIGIN_OPTIONS, PLAN_OPTIONS, SALE_PRESETS, TRIAL_OPTIONS, UNIVERSE_OPTIONS,
  applySalePreset, applySaleRange, cleanOnboardingFilters, previousSalePeriod, resolveSalePeriod, salePeriodText, statusOptionsFor,
} from "./piOnboardingFilters.js";
import { formatPeriod } from "./piFormat.js";

let sequence = 0;

const without = (options, value) => options.filter((option) => option.value !== value);

export default {
  name: "pi-onboarding-filters",
  components: { dateRangeFilter, filterSelect, piDefinitionTooltip },
  props: {
    value: { type: Object, required: true },
  },
  data() {
    sequence += 1;
    return {
      ids: { period: "pi-obf-period-" + sequence, shard: "pi-obf-shard-" + sequence },
      customRequested: false,
      presets: SALE_PRESETS,
      maxDays: MAX_SALE_RANGE_DAYS,
      cohortOptions: without(COHORT_OPTIONS, "month"),
      universeOptions: without(UNIVERSE_OPTIONS, "sold"),
      trialOptions: without(TRIAL_OPTIONS, "all"),
      planOptions: PLAN_OPTIONS,
      journeyOptions: JOURNEY_OPTIONS,
      originOptions: ORIGIN_OPTIONS,
      startedNote: "Iniciou o uso = cadastrou colaboradores até D+60 (ou até hoje, se a janela ainda está aberta). "
        + "Plano, porte, perfil, jornada e situação são os de hoje.",
    };
  },
  computed: {
    state() {
      return cleanOnboardingFilters(this.value);
    },
    period() {
      return resolveSalePeriod(this.state, new Date());
    },
    statusOptions() {
      return without(statusOptionsFor(this.state.universe), "all");
    },
    customOpen() {
      return this.customRequested || !!this.state.saleFrom || !!this.state.saleTo;
    },
    periodText() {
      return salePeriodText(this.period);
    },
    fallbackText() {
      return formatPeriod(this.period.from, this.period.to);
    },
    compareOn() {
      return compareEnabled(this.value);
    },
    previousText() {
      const previous = previousSalePeriod(this.period);
      return previous ? formatPeriod(previous.from, previous.to) : "";
    },
  },
  methods: {
    emit(next) {
      this.$emit("input", next);
    },
    set(key, raw) {
      const patch = {};
      patch[key] = raw === null || raw === undefined ? "" : String(raw);
      this.emit(Object.assign({}, this.value, patch));
    },
    choosePreset(preset) {
      this.customRequested = false;
      this.emit(applySalePreset(this.value, preset));
    },
    openCustom() {
      this.customRequested = true;
      if (!this.state.saleFrom && !this.state.saleTo) this.emit(applySaleRange(this.value, this.period.from, this.period.to));
    },
    setRange(from, to) {
      this.emit(applySaleRange(this.value, from, to));
    },
    setCompare(enabled) {
      this.emit(applyCompare(this.value, enabled));
    },
  },
};
</script>

<style src="./pi.css"></style>

<style scoped>
.pi-obf {
  margin-bottom: 10px;
  padding: 12px 14px;
  border: 1px solid #e6e7eb;
  border-radius: 6px;
  background: #fafbfc;
}

.pi-obf__row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px 20px;
}

.pi-obf__row + .pi-obf__row {
  margin-top: 12px;
}

.pi-obf__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.pi-obf__label {
  display: block;
  font-size: 12px;
  font-weight: 400;
  color: #4e5560;
}

.pi-obf__hint {
  font-size: 11px;
  color: #6b7280;
}

.pi-obf__error {
  font-size: 12px;
  color: #a3172b;
}

.pi-obf__range {
  margin-top: 6px;
}

.pi-obf__check {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-size: 12px;
  font-weight: 400;
  color: #3a424e;
}

.pi-obf__check--inline {
  padding-bottom: 8px;
}

.pi-obf__labelled {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.pi-obf__labelled .pi-obf__label {
  margin: 0;
}

.pi-obf__info {
  margin-left: 4px;
  vertical-align: middle;
}

.pi-obf__shard {
  width: 120px;
}

.pi-obf__note {
  margin: 10px 0 0;
  font-size: 11px;
  color: #6b7280;
}

@media (max-width: 767px) {
  .pi-obf__row {
    flex-direction: column;
    align-items: stretch;
  }

  .pi-obf__shard {
    width: 100%;
  }
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/components/pi/piOnboardingFilters.vue